import React, { MouseEventHandler, useCallback, useState } from 'react';
import ZoomableImageToolbar from './Components/ZoomableImageToolbar';
import ZoomableImage from './Components/ZoomableImage';
import ZoomableImageFullscreen from './Components/ZoomableImageFullscreen';
import { TransformWrapper, ReactZoomPanPinchState } from 'react-zoom-pan-pinch';
import EDBox from '../../BaseComponents/EDBox/EDBox';
import { ZOOMABLEIMAGE_CENTERED_IMAGE } from '../../Utils/LocalStorage/localStorageKeys';

interface IProps {
  imageCount: number;
  imageIndex: number;
  isImage: boolean;
  nextImage: () => void;
  previousImage: () => void;
  title: string;
  navHeader: string;
  subtitle?: string;
  showCenteredImageToggle?: boolean;
  noFullscreenToggle?: boolean;
  noImageCount?: boolean;
  centeredImage?: boolean;
  extraBottomPadding?: boolean;
  disableNavHeader?: boolean;
  disableNavigation?: boolean;
  navHeaderEttGenus?: boolean;
  paperRef?: React.MutableRefObject<HTMLDivElement | undefined>;
  setPaperRef?: (element: HTMLDivElement) => void;
  setPaperRefFullscreen?: (element: HTMLDivElement) => void;
  fullScreenRender?: React.ReactNode;
  onContextMenu?: MouseEventHandler<HTMLDivElement>;
  useWheelToScroll: boolean;
  ignoreFloatingMenu?: boolean;
}

/**
 * Visar en bild med en overlay av verktyg för att kunna zomma in, flytta bilden m.m.
 */
const ZoomableImageView: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const [showFullScreen, setShowFullscreen] = useState<boolean>(false);
  const [rotation, setRotation] = useState<number>(0);
  const [centered, setCentered] = useState<boolean | undefined>(
    props.centeredImage && props.showCenteredImageToggle
      ? localStorage.getItem(ZOOMABLEIMAGE_CENTERED_IMAGE) === 'true'
        ? true
        : false
      : props.centeredImage
  );
  const [panning, setPanning] = useState(false);

  const toggleFullScreen = useCallback(() => {
    setShowFullscreen(!showFullScreen);
  }, [showFullScreen]);

  const toggleCenteredImage = useCallback(() => {
    localStorage.setItem(ZOOMABLEIMAGE_CENTERED_IMAGE, `${!centered}`);
    setCentered(!centered);
  }, [centered]);

  const isAtDefault = useCallback(
    (state: ReactZoomPanPinchState) => (): boolean => {
      const notZoomed = state.scale === 1;
      const notPanned = state.positionX === 0 && state.positionY === 0;
      return notZoomed && notPanned;
    },
    []
  );

  const rotateRight = useCallback(() => {
    setRotation(rotation + 90);
  }, [rotation]);

  const rotateLeft = useCallback(() => {
    setRotation(rotation - 90);
  }, [rotation]);

  const rotationReset = useCallback(() => {
    setRotation(0);
  }, []);

  return (
    <EDBox component={'span'} onContextMenu={props.onContextMenu}>
      <TransformWrapper
        initialScale={1}
        limitToBounds={false}
        minScale={0.1}
        doubleClick={{
          disabled: true,
        }}
        wheel={{
          disabled: props.useWheelToScroll,
        }}
        velocityAnimation={{ disabled: true }}
        alignmentAnimation={{ disabled: true }}
        onPanningStart={() => {
          if (!panning) {
            setPanning(true);
          }
        }}
        onPanningStop={() => {
          if (panning) {
            setPanning(false);
          }
        }}
      >
        {({ zoomIn, zoomOut, resetTransform, state, instance }) => (
          <>
            <ZoomableImage
              rotation={rotation}
              rotationReset={() => rotationReset()}
              panning={panning}
              toolbar={
                <ZoomableImageToolbar
                  defaultZoom={() => resetTransform()}
                  isAtDefault={isAtDefault(state)}
                  zoomIn={() => zoomIn()}
                  zoomOut={() => zoomOut()}
                  rotateRight={() => rotateRight()}
                  rotateLeft={() => rotateLeft()}
                  toggleCenteredImage={() => {
                    toggleCenteredImage();
                    resetTransform();
                  }}
                  toggleFullScreen={toggleFullScreen}
                  fullScreen={showFullScreen}
                  imageCount={props.imageCount}
                  imageIndex={props.imageIndex}
                  nextImage={props.nextImage}
                  previousImage={props.previousImage}
                  showCenteredImageToggle={
                    props.centeredImage && props.showCenteredImageToggle
                  }
                  noFullscreenToggle={props.noFullscreenToggle}
                  noImageCount={props.noImageCount}
                  ignoreFloatingMenu={props.ignoreFloatingMenu}
                />
              }
              centered={centered}
              setPaperRef={props.setPaperRef}
              extraBottomPadding={props.extraBottomPadding}
              ignoreFloatingMenu={props.ignoreFloatingMenu}
              disableNavigation={props.disableNavigation}
              itemCount={props.imageCount}
              itemIndex={props.imageIndex}
              isImage={props.isImage}
              onNextClick={props.nextImage}
              onPrevClick={props.previousImage}
              navHeader={props.navHeader}
              navHeaderEttGenus={props.navHeaderEttGenus}
              useWheelToScroll={props.useWheelToScroll}
            >
              {props.children}
            </ZoomableImage>

            <ZoomableImageFullscreen
              rotation={rotation}
              navHeader={props.navHeader}
              navHeaderEttGenus={props.navHeaderEttGenus}
              disableNavHeader={props.disableNavHeader}
              imageCount={props.imageCount}
              imageIndex={props.imageIndex}
              isImage={props.isImage}
              nextImage={props.nextImage}
              previousImage={props.previousImage}
              showFullScreen={showFullScreen}
              toggleCenteredImage={toggleCenteredImage}
              toggleFullScreen={toggleFullScreen}
              title={props.title}
              subtitle={props.subtitle}
              noImageCount={props.noImageCount}
              showCenteredImageToggle={
                props.centeredImage && props.showCenteredImageToggle
              }
              centeredImage={centered}
              rotateRight={() => rotateRight()}
              rotateLeft={() => rotateLeft()}
              rotationReset={() => rotationReset()}
              setPaperRef={props.setPaperRefFullscreen}
              useWheelToScroll={props.useWheelToScroll}
            >
              {props.fullScreenRender !== undefined
                ? props.fullScreenRender
                : props.children}
            </ZoomableImageFullscreen>
          </>
        )}
      </TransformWrapper>
    </EDBox>
  );
};

export default ZoomableImageView;
